import { defineStore } from 'pinia';
import { setUserInfo, clearUserInfo, getUserInfo } from '@/utils/auth';
import { removeRouteListener } from '@/utils/route-listener';
import { clear } from '@/store/modules/supplier/auth';
import { isElectron } from '@/utils/electronFunc';
import {
  initBaseData,
  initPriceData,
} from '@/views/basis/sysConfig/constants/integrityCheck';
import { getIntegrityCheckArr } from '@/views/basis/sysConfig/common';
import { cloneDeep, isUndefined } from 'lodash';
import { removeScrollListener } from '@/utils/scroll-position-listener';
import { jstSyncDefaultConfig } from '@/views/basis/thirdparty/constants';
import { defaultMenuNames } from '@/views/home/constants';
import { UserState, UserGetters, UserActions, Flow } from './types';
import thirdpartyConfig from './thirdpartyConfig';
// import useAppStore from '../app';

const userInfo = getUserInfo();

const useUserStore = defineStore<'user', UserState, UserGetters, UserActions>(
  'user',
  {
    state: (): UserState => ({
      // 用户登录返回的信息
      userLoginInfo: userInfo || {},
      // currentUser.ac 的信息
      userInfo: undefined,
      permissions: [],
      permissionRemarkMap: {},
      usernameMap: {},
      companyInfo: undefined,
      companyConfig: [],
      userConfig: [],
      // 用户token
      userToken: '',
      role: '',
      // 用户系统配置
      sysConfigList: [],
      // 用户系统配置: configKey-configValue
      sysConfigMap: {},
      enablePaymentServiceMap: {},
      // 用户配置
      userConfigList: [],
      // 用户配置 configKey-configValue
      userConfigMap: {},
      // 用户配置自定义工序节点列--生产明细
      userProcessColumn: [],
      // 第三方对接
      thirdpartyInfo: [],
      // 流程配置
      flowConfigList: [],
      restDaysList: [],
      ...thirdpartyConfig.thirdpartyInfo,
      /* // 聚水潭
      jushuitan: {
        // authState:  0-未授权 1-授权中 2-已取消 3-已过期
      },
      kuaimai: {
        // authState:  0-未授权 1-授权中 2-已取消 3-已过期
      }, */
    }),

    getters: {
      // 大货商品可编辑修改
      isAllowGoodsEdit(state: UserState) {
        return !!Number(state.sysConfigMap?.allowGoodsEdit || 1);
      },
      isOpenStockByCloth(state: UserState) {
        const configList = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return !!configList.find(
          (item: any) => item.configKey === 'stockByCloth'
        )?.enable;
      },
      isOpenGoodsSnGenerate(state: UserState) {
        return !!Number(state.sysConfigMap?.goodsSnGenerateEnable || 0);
      },
      isHasFlagShip(state: UserState) {
        const { professional, flagShip } = state.enablePaymentServiceMap || {};
        return !!flagShip;
      },
      PMSTableRangeListConfig(state: UserState) {
        const configList = state.userConfigList;
        const defaultConfig = { enable: 0, configValue: '[]' };
        if (!Array.isArray(configList)) {
          return defaultConfig;
        }
        const config = configList.find(
          (item: any) => item.configKey === 'syncPMSTableRangeList'
        );
        if (!config) {
          return defaultConfig;
        }
        return {
          enable: config.enable,
          configValue: config.configValue || '[]',
        };
      },
      // 是否使用流畅模式
      isUseSmoothMode(state: UserState) {
        const configValue = state.userConfigMap?.isUseSmoothMode as string;
        if (!configValue) return 0;
        return Number(configValue);
      },
      // 自定义菜单
      homeMenus(state: UserState) {
        const configValue = state.userConfigMap?.homeMenusByPC as string;
        if (configValue) {
          return JSON.parse(configValue);
        }
        return cloneDeep(defaultMenuNames);
      },
      // 自定义板块
      customBlockConfig(state: UserState) {
        const configValue = state.userConfigMap?.customBlockConfig as string;
        if (configValue) {
          return JSON.parse(configValue);
        }
        return null;
      },
      // 快麦是否允许预约入库
      isAllowKmPreIn(state: UserState): boolean {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        const config = configList.find(
          (item: any) => item.configKey === 'allowKmPreIn'
        );
        if (!config) {
          return false;
        }
        return JSON.parse(config.configValue ?? 0);
      },
      productionMonitorScreenCompanyInfo(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return {};
        }
        const config = configList.find(
          (item: any) => item.configKey === 'customProductionMonitorScreen'
        );
        if (!config) {
          return {};
        }
        const { enable, configValue } = config;
        return {
          enable,
          configValue: JSON.parse(configValue),
        };
      },
      // 聚水潭自动同步商品
      hasAutoUpdateJstGoods(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return configList.find(
          (item: any) => item.configKey === 'autoUpdateJstGoods'
        )?.enable;
      },
      // 聚水潭同步配置
      jstSyncConfig(state: UserState) {
        const configList: any = state.sysConfigList;
        const defaultConfig = cloneDeep(jstSyncDefaultConfig);
        if (!Array.isArray(configList)) {
          return defaultConfig;
        }
        const config = configList.find(
          (item: any) => item.configKey === 'jstSyncConfig'
        );
        if (!config) {
          return defaultConfig;
        }
        return JSON.parse(config.configValue || '{}');
      },
      // 是否开启默认采购单价
      hasDefaultPurchasePrice() {
        const cfg: any = this.jstSyncConfig;
        return (
          cfg.purchaseSyncPriceType &&
          (cfg.purchaseSyncPricePercent || cfg.purchaseSyncPricePercent === 0)
        );
      },
      // 是否展示生产批次
      hasProduceBatch(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return !!configList.find(
          (item: any) => item.configKey === 'produceBatchSnGenerate'
        )?.enable;
      },
      // 开启设计款/商品资料完整性检查的配置
      hasDesignSnAndGoodsCheck(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return null;
        }
        const config = configList.find(
          (item: any) => item.configKey === 'designSnAndGoodsCheck'
        );
        if (!config?.enable) {
          return null;
        }
        return JSON.parse(config.configValue);
      },
      // 开启设计款/商品资料完整性检查且允许临时保存
      hasIntegrityCheckAllowTempSave() {
        if (!this.hasDesignSnAndGoodsCheck) return false;
        const { allowTempSave = 1 } = this.hasDesignSnAndGoodsCheck;
        return !!allowTempSave;
      },
      // 完整性基础字段是否必填
      integrityCheckBaseInfoMap() {
        if (!this.hasDesignSnAndGoodsCheck) return {};
        const { baseInfo = '' } = this.hasDesignSnAndGoodsCheck;
        const baseInfoTemp: Recordable[] = getIntegrityCheckArr(
          baseInfo,
          initBaseData
        );
        return baseInfoTemp.reduce((pre: Recordable, cur: Recordable) => {
          pre[cur.field] = cur.status;
          return pre;
        }, {});
      },
      integrityCheckPriceInfoMap() {
        if (!this.hasDesignSnAndGoodsCheck) return {};
        const { priceInfo = '' } = this.hasDesignSnAndGoodsCheck;
        const priceInfoTemp: Recordable[] = getIntegrityCheckArr(
          priceInfo,
          initPriceData
        );
        return priceInfoTemp.reduce((pre: Recordable, cur: Recordable) => {
          pre[cur.field] = cur.status;
          return pre;
        }, {});
      },
      // 是否开启转大货审核
      hasConvertGoodsAudit(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return !!configList.find(
          (item: any) => item.configKey === 'convertGoodsAudit'
        )?.enable;
      },
      // 是否设计款自动同步至商品
      hasDesignSnToGoodsAutoSync(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return !!configList.find(
          (item: any) => item.configKey === 'designSnToGoodsAutoSync'
        )?.enable;
      },
      // 是否开启设计款/商品附件自动同步
      hasDesignSnAndGoodsSyncAttachment(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return !!configList.find(
          (item: any) => item.configKey === 'designSnAndGoodsSyncAttachment'
        )?.enable;
      },
      // 生产批次生产规则是否是按照货号
      isGoodsSnProBatchType(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        const configData =
          configList.find(
            (item: any) => item.configKey === 'produceBatchSnGenerate'
          )?.configValue || '';
        const type = configData ? JSON.parse(configData)?.type || 2 : 2;
        return type === 3;
      },
      getUserInfo(state: UserState): UserState {
        return { ...state };
      },

      // 物料小数位
      materialDecimal(state: UserState): number {
        let materialDecimalVal = 2;
        state.sysConfigList.forEach((item: Record<string, any>) => {
          if (item.configKey === 'materialDecimal' && item.configValue) {
            materialDecimalVal = Number(item.configValue);
          }
        });
        return materialDecimalVal;
      },
      // 系统控制的物料多规格开关
      isOpenMaterialMulSpec(state: UserState): number {
        let isOpenMaterialMulSpecVal = 0;
        state.sysConfigList.forEach((item: Record<string, any>) => {
          if (item.configKey === 'isOpenMaterialMulSpec' && item.configValue) {
            isOpenMaterialMulSpecVal = Number(item.configValue);
          }
        });
        return isOpenMaterialMulSpecVal;
      },
      // 系统控制的【入库需要复核】开关，默认关
      isOpenRecheckStoreIn(state: UserState): number {
        const recheckStoreInConfig = state.sysConfigList.find(
          (item: Recordable) => {
            return item.configKey === 'recheckStoreIn';
          }
        );
        if (recheckStoreInConfig) {
          return Number((recheckStoreInConfig as Recordable).configValue);
        }
        return 0;
      },
      // 超量按总数比较，默认关
      isScanTicketCompareTypeTotal(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return !!configList.find(
          (item: any) => item.configKey === 'scanTicketCompareTypeTotal'
        )?.enable;
      },
      // 系统控制的【入库需要复核】开关，默认关
      isOpenMaterialIssue(state: UserState): number {
        const isOpenMaterialIssueConfig = state.sysConfigList.find(
          (item: Recordable) => {
            return item.configKey === 'isOpenMaterialIssue';
          }
        );
        if (isOpenMaterialIssueConfig) {
          return Number((isOpenMaterialIssueConfig as Recordable).configValue);
        }
        return 0;
      },
      // isOpenProdStoreIn(state: UserState): number {
      //   const prodStoreInConfig = state.sysConfigList.find(
      //     (item: Recordable) => {
      //       return item.configKey === 'excessLimitInboundNumber';
      //     }
      //   );
      //   if (prodStoreInConfig) {
      //     return Number((prodStoreInConfig as Recordable).configValue);
      //   }
      //   return 0;
      // },
      // 生产入库规则
      produceStoreInRule(state: UserState): Recordable {
        const initData: Recordable = {
          cutBedIsSku: 0, // 裁床节点分色码 1-是 0-否
          storeInExcessLimit: undefined, // 生产入库超量上限
          storeInExcessUnit: 1, // 生产入库超量单位 1-% 2-件
          produceFinishExcessLowerLimit: undefined, // 生产完成超量下限
          produceFinishExcessUnit: 1, // 生产完成超量单位 1-% 2-件
          privilegedUserIds: [], // 特权用户
          // 以下是configValue中没有的字段
          privilegedUserList: [],
          storeInExcessEnable: 1, // 是要校验超量入库限制
          finishExcessEnable: 1, // 是否校验生产完成限制
          isPrivileged: false, // 是否是特权用户
        };
        const prodStoreInConfig = state.sysConfigList.find(
          (item: Recordable) => {
            return item.configKey === 'produceStoreInRule';
          }
        );
        if (prodStoreInConfig) {
          Object.assign(
            initData,
            JSON.parse((prodStoreInConfig as Recordable).configValue)
          );
        }
        const uid = state.userLoginInfo?.id?.toString();
        initData.isPrivileged = !!initData.privilegedUserIds.some(
          (id: any) => id.toString() === uid
        );
        // 改为不限制
        if (isUndefined(initData.storeInExcessLimit)) {
          initData.storeInExcessEnable = 0;
        }
        if (isUndefined(initData.produceFinishExcessLowerLimit)) {
          initData.finishExcessEnable = 0;
        }
        return initData;
      },
      // BOM需求未锁定时采购提醒
      bomLockRemindBypurchase(state: UserState): number {
        const config = state.sysConfigList.find((item: Recordable) => {
          return item.configKey === 'bomLockRemindBypurchase';
        });
        if (config) {
          return Number((config as Recordable).configValue);
        }
        return 0;
      },
      // 是否专业版迁移到旗舰版
      isSampleToDesign(state: UserState) {
        const { sysConfigList } = state;
        if (!Array.isArray(sysConfigList)) {
          return false;
        }
        return !!sysConfigList.find(
          (item: Recordable) => item.configKey === 'sampleToDesignFinish'
        );
      },
      ...thirdpartyConfig.thirdpartyGetters,
      /*       // 当前是否有授权聚水潭
      isAuthByJST(state: UserState): boolean {
        return state.jushuitan?.authState === 1;
      },
      // 当前是否有授权快麦
      isAuthByKM(state: UserState): boolean {
        return state.kuaimai?.authState === 1;
      },
      // 是否有第三方授权
      isAuthByThirdparty(state: UserState) {
        return state.thirdpartyInfo.find((item) => item.authState === 1);
      },
      // 是否授权聚水潭，并且授权未过期
      isAuthValidByJST(state: UserState): () => boolean {
        return () => {
          if (state.jushuitan?.authState !== 1) return false;
          return state.jushuitan.expirationTime - Date.now() > 0;
        };
      }, */
      // // 有授权过聚水潭，无论是否取消了还是过期了
      // hasAuthorizedJST(state: UserState): boolean {
      //   return !!state.jushuitan.expirationTime;
      // },
      // 绑定聚水潭的品牌
      bindBrandListByJST(state: UserState) {
        if (!this.isAuthByJST) return [];
        const configValue = state.sysConfigMap?.jstGoodsBrand as string;
        if (!configValue) return [];
        return JSON.parse(configValue);
      },
      getDefaultProdType(state: UserState): number {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return 3; // 默认是委外加工类型
        }
        return Number(
          configList.find((item: any) => item.configKey === 'defaultProdType')
            ?.configValue
        );
      },
      isOpenLastStoreInStoreHouse(state: UserState): boolean {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return !!configList.find(
          (item: any) => item.configKey === 'defaultStoreInStorehouse'
        )?.enable;
      },
      isOpenLastMaterialStoreHouse(state: UserState): boolean {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) {
          return false;
        }
        return !configList.find(
          (item: any) => item.configKey === 'materialStoreHouseStrategy'
        )?.enable;
      },
      workDayTimeSettingConfig(state: UserState) {
        const configList: any = state.sysConfigList;
        if (!Array.isArray(configList)) return {};
        const config = configList.find(
          (item: any) => item.configKey === 'workdayTimeSettingConfig'
        );
        if (!config) return {};
        return JSON.parse(config.configValue || '{}');
      },
      // 生产排产规则
      schedulingRule(state: UserState) {
        const configValue = state.sysConfigMap?.schedulingRule as string;
        if (!configValue) return { workDayPolicy: 0, setupTime: 0 };
        return JSON.parse(configValue);
      },
    },

    actions: {
      setUserProcessColumn(processColumn: any[]) {
        this.userProcessColumn = processColumn;
      },
      setUserConfigMap(configList: Array<any> = []) {
        const userConfigMap: Recordable = {};
        if (Array.isArray(configList)) {
          configList.forEach((config: any) => {
            const { configKey, configValue } = config;
            userConfigMap[configKey] = configValue;
          });
        }
        this.userConfigMap = userConfigMap;
      },
      setSysConfigMap(configList: Array<any> = []) {
        const sysConfigMap: Recordable = {};
        if (Array.isArray(configList)) {
          configList.forEach((config: any) => {
            const { configKey, configValue } = config;
            sysConfigMap[configKey] = configValue;
            if (['goodsSnGenerate', 'designSnGenerate'].includes(configKey)) {
              sysConfigMap[`${configKey}Enable`] = Number(config.enable);
            }
          });
        }
        this.sysConfigMap = sysConfigMap;
      },
      updateThirdparty(info: Recordable[] = []) {
        this.thirdpartyInfo = info;
        this.jushuitan =
          info?.find((item: Recordable) => item.sourceKey === 'JUSHUITAN') ??
          {};
        this.kuaimai =
          info?.find((item: Recordable) => item.sourceKey === 'KUAIMAI') ?? {};
      },
      switchRoles() {
        return new Promise((resolve) => {
          resolve({});
        });
      },
      // Set user's information
      setInfo(partial: Partial<UserState>) {
        this.$patch(partial);
        if (partial.sysConfigList) this.setSysConfigMap(partial.sysConfigList);
        if (partial.userConfigList)
          this.setUserConfigMap(partial.userConfigList);
      },
      // Reset user's information
      resetInfo() {
        this.$reset();
      },

      // Get user's information
      async info() {
        // const res = await getUserInfo();
        // this.setInfo(res.data);
      },

      // Login
      async loginCallback(res: any) {
        try {
          this.userLoginInfo = res.data;
          setUserInfo(res.data);
          clear(); // 清除加工厂登录缓存
        } catch (err) {
          clearUserInfo();
          throw err;
        }
      },
      logoutCallBack() {
        // const appStore = useAppStore();
        // this.resetInfo();
        clearUserInfo();
        removeScrollListener();
        removeRouteListener();
        // appStore.clearServerMenu();
      },
      // Logout
      async logout() {
        // try {
        //   await userLogout();
        // } finally {
        //   this.logoutCallBack();
        // }
        this.logoutCallBack();
        if (isElectron()) {
          setTimeout(() => {
            window.localStorage.clear();
            window.location.reload(); // 刷新当前页面
          }, 200);
          return;
        }
        window.location.reload();
      },
      updateFlowConfig(configList: Flow[] = []) {
        this.flowConfigList = configList;
      },
      updateRestDays(restDaysList: Recordable[] = []) {
        this.restDaysList = restDaysList;
      },
    },
  }
);

export default useUserStore;
